import React, { useState, useEffect } from 'react';
import { ICONS } from '../../constants';
import type { View } from '../../App';
import { Button } from '../ui/Button';
import { useSession } from '../../context/SessionContext';

interface SidebarProps {
  currentView: View;
  setView: (view: View) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
}

interface NavItemProps {
  icon: React.ReactElement;
  label: string;
  isActive: boolean;
  isCollapsed: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, isCollapsed, onClick }) => (
  <li>
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={isCollapsed ? label : ''}
      className={`flex items-center p-3 rounded-lg transition-all duration-300 ${
        isCollapsed ? 'justify-center px-0' : ''
      } ${
        isActive
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
      }`}
    >
      {React.cloneElement(icon as React.ReactElement<any>, { className: 'w-6 h-6 flex-shrink-0' })}
      {!isCollapsed && <span className="ml-3 font-medium whitespace-nowrap overflow-hidden">{label}</span>}
    </a>
  </li>
);

export const Sidebar: React.FC<SidebarProps> = ({ currentView, setView, isOpen, setIsOpen, isCollapsed, setIsCollapsed }) => {
  const { session } = useSession();
  const isAdmin = session.user?.role === 'admin';
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    const settingsViews: Array<View['type']> = ['drivers', 'admins', 'vehicles'];
    if (settingsViews.includes(currentView.type)) {
      setIsSettingsOpen(true);
    }
  }, [currentView]);

  const handleItemClick = (view: View) => {
    setView(view);
    setIsOpen(false); // Fecha a sidebar ao selecionar um item no modo mobile
  };

  const [isLightMode, setIsLightMode] = useState(() => {
    return localStorage.getItem('theme') === 'light';
  });

  const toggleTheme = () => {
    if (isLightMode) {
      document.body.classList.remove('light-theme');
      localStorage.setItem('theme', 'dark');
      setIsLightMode(false);
    } else {
      document.body.classList.add('light-theme');
      localStorage.setItem('theme', 'light');
      setIsLightMode(true);
    }
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      ></div>
      
      {/* Main Sidebar */}
      <aside className={`${isCollapsed ? 'w-20' : 'w-64'} bg-slate-900 border-r border-white/5 p-4 flex flex-col fixed top-0 left-0 h-full z-40 transform transition-all duration-300 lg:translate-x-0 print:hidden overflow-y-auto ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        
        {/* Toggle Button for Desktop */}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex absolute right-3 top-6 w-6 h-6 bg-slate-800 border border-white/10 rounded-full items-center justify-center text-slate-400 hover:text-white z-50 transition-all hover:scale-110"
        >
          <ICONS.chevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-90'}`} />
        </button>

        <div className={`flex flex-col items-center mb-8 ${isCollapsed ? 'px-0' : 'px-2'}`}>
          {!isCollapsed ? (
             <img src="assets/logo.png?v=1.3" alt="Logo" className="w-full h-auto object-contain mb-3 rounded-lg" onError={(e) => (e.currentTarget.style.display = 'none')} />
          ) : (
             <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-black text-xl mb-3">V</div>
          )}
          {!isCollapsed && (
            <div className="text-center animate-in fade-in slide-in-from-top-1 duration-500">
              <p className="text-[10px] text-blue-400 font-bold uppercase tracking-[0.2em] -mt-1">Sistema de Gestão</p>
            </div>
          )}
        </div>

        <nav>
          <ul className="space-y-2">
            <NavItem
              icon={<ICONS.dashboard />}
              label="Dashboard"
              isActive={currentView.type === 'dashboard'}
              isCollapsed={isCollapsed}
              onClick={() => handleItemClick({ type: 'dashboard' })}
            />
            <NavItem
              icon={<ICONS.trip />}
              label="Viagens"
              isActive={currentView.type === 'tripList' || currentView.type === 'newTrip' || currentView.type === 'editTrip' || currentView.type === 'viewTrip' || currentView.type === 'activeTrip'}
              isCollapsed={isCollapsed}
              onClick={() => handleItemClick({ type: 'tripList' })}
            />
            {isAdmin && (
              <>
                <NavItem
                  icon={<ICONS.currencyDollar />}
                  label="Contas a Pagar"
                  isActive={currentView.type === 'accountsPayable'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'accountsPayable' })}
                />
                <NavItem
                  icon={<ICONS.chartBar />}
                  label="Faturamento"
                  isActive={currentView.type === 'billing'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'billing' })}
                />
                <NavItem
                  icon={<ICONS.chartPie />}
                  label="Análise de Frota"
                  isActive={currentView.type === 'analysis'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'analysis' })}
                />
                <NavItem
                  icon={<ICONS.bank />}
                  label="Financeiro"
                  isActive={currentView.type === 'financial'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'financial' })}
                />
                <NavItem
                  icon={<ICONS.currencyDollar />}
                  label="Adiantamentos"
                  isActive={currentView.type === 'advances'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'advances' })}
                />
                <NavItem
                  icon={<ICONS.truck />}
                  label="Manutenção"
                  isActive={currentView.type === 'maintenance'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'maintenance' })}
                />
                <NavItem
                  icon={<ICONS.gasPump />}
                  label="Abastecimento"
                  isActive={currentView.type === 'fueling'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'fueling' })}
                />
                <NavItem
                  icon={<ICONS.chartBar />}
                  label="Relat. Combustível"
                  isActive={currentView.type === 'fuelingDashboard'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'fuelingDashboard' })}
                />
                <NavItem
                  icon={<ICONS.document />}
                  label="Emissão CT-e"
                  isActive={currentView.type === 'cte'}
                  isCollapsed={isCollapsed}
                  onClick={() => handleItemClick({ type: 'cte' })}
                />
              </>
            )}
          </ul>
        </nav>

        <div className="mt-auto pt-6">
          {isAdmin && (
             <nav className="mb-4">
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                      title={isCollapsed ? 'Configurações' : ''}
                      className={`flex items-center justify-between w-full p-3 rounded-lg text-slate-300 hover:bg-slate-700/50 hover:text-white transition-all duration-300 ${isCollapsed ? 'justify-center px-0' : ''}`}
                    >
                      <div className="flex items-center">
                        <ICONS.settings className="w-6 h-6 flex-shrink-0" />
                        {!isCollapsed && <span className="ml-3 font-medium whitespace-nowrap">Configurações</span>}
                      </div>
                      {!isCollapsed && <ICONS.chevronDown className={`w-5 h-5 transition-transform ${isSettingsOpen ? 'rotate-180' : ''}`} />}
                    </button>
                    {isSettingsOpen && !isCollapsed && (
                      <ul className="pl-6 mt-2 space-y-2 animate-in fade-in slide-in-from-left-2 duration-300">
                        <NavItem
                          icon={<ICONS.driver />}
                          label="Motoristas"
                          isActive={currentView.type === 'drivers'}
                          isCollapsed={isCollapsed}
                          onClick={() => handleItemClick({ type: 'drivers' })}
                        />
                        <NavItem
                          icon={<ICONS.users />}
                          label="Administradores"
                          isActive={currentView.type === 'admins'}
                          isCollapsed={isCollapsed}
                          onClick={() => handleItemClick({ type: 'admins' })}
                        />
                        <NavItem
                          icon={<ICONS.vehicle />}
                          label="Veículos"
                          isActive={currentView.type === 'vehicles'}
                          isCollapsed={isCollapsed}
                          onClick={() => handleItemClick({ type: 'vehicles' })}
                        />
                      </ul>
                    )}
                  </li>
                </ul>
            </nav>
          )}

          <div className="flex gap-2 mb-4">
            <button 
              onClick={toggleTheme} 
              className={`flex-1 ${isCollapsed ? 'p-0 h-10 flex items-center justify-center' : 'p-2 flex items-center justify-center'} bg-slate-800 text-slate-400 hover:text-white rounded-lg border border-white/5 transition-all`}
              title="Alternar Tema (Claro/Escuro)"
            >
              {!isCollapsed ? (
                <span className="flex items-center gap-2 text-sm font-medium">
                  {isLightMode ? <ICONS.moon className="w-4 h-4" /> : <ICONS.sun className="w-4 h-4" />}
                  Tema
                </span>
              ) : (
                isLightMode ? <ICONS.moon className="w-5 h-5" /> : <ICONS.sun className="w-5 h-5" />
              )}
            </button>
          </div>

          <Button 
            onClick={() => handleItemClick({ type: 'newTrip' })} 
            className={`w-full ${isCollapsed ? 'p-0 h-10 flex items-center justify-center' : ''}`}
            title={isCollapsed ? 'Nova Viagem' : ''}
          >
            <ICONS.plus className={`${isCollapsed ? 'w-6 h-6' : 'w-5 h-5 mr-2'}`} />
            {!isCollapsed && <span>Nova Viagem</span>}
          </Button>
        </div>

        {/* Developer Branding */}
        {!isCollapsed && (
          <div className="mt-6 pt-4 border-t border-slate-700/50 flex flex-col items-center opacity-40 hover:opacity-100 transition-all duration-500 animate-in fade-in">
            <p className="text-[8px] uppercase tracking-[0.3em] text-slate-500 mb-2 font-bold">Desenvolvido por</p>
            <img 
              src="assets/logo-dev.png" 
              alt="Dev Logo" 
              className="h-6 w-auto grayscale contrast-125 hover:grayscale-0 transition-all duration-700 pointer-events-none"
              onError={(e) => (e.currentTarget.parentElement!.style.display = 'none')}
            />
          </div>
        )}
      </aside>
    </>
  );
};